/**
 * A library for working with SockJS websockets.
 *
 * This is somewhat based on rxjs-websockets but with a lot of customisations
 * for freelancer.
 */
import {
  ErrorHandler,
  Inject,
  Injectable,
  NgZone,
  OnDestroy,
  Optional,
} from '@angular/core';
import { AppState } from '@capacitor/app';
import { Auth } from '@freelancer/auth';
import { Pwa } from '@freelancer/pwa';
import { Timer, TimeUtils } from '@freelancer/time-utils';
import { Tracking } from '@freelancer/tracking';
import { toNumber } from '@freelancer/utils';
import { Store } from '@ngrx/store';
import { ResourceTypeApi } from 'api-typings/gotifications/gotifications';
import * as Rx from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  filter,
  first,
  map,
  mapTo,
  pairwise,
  publishReplay,
  refCount,
  skip,
  startWith,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { TypedAction } from '../actions';
import { DATASTORE_CONFIG } from '../datastore.config';
import { DatastoreConfig } from '../datastore.interface';
import { isEqual, setDiff } from '../helpers';
import { startWithEmptyList } from '../operators';
import { StoreState } from '../store.model';
import {
  SockMessageSend,
  WebsocketChannel,
  WebsocketResource,
} from './message-send-event.model';
import { MultiSet } from './multiset';
import {
  isWebsocketMessage,
  isWebsocketSubscriptionExpiring,
} from './server-event.helpers';
import {
  ExpiringSubscription,
  WebsocketMessage,
  WebsocketServerEvent,
  WebsocketServerEventTParam,
} from './server-event.model';
import { BaseServerMessage } from './server.types';
import {
  generateChannelSubMessage,
  generateHeader,
  generateOnlineOfflineSub,
  generateOnlineOfflineUnsub,
  generateResourcesSubMessage,
  generateResourcesUnsubMessage,
  Socket,
  State,
} from './sock-js';
import {
  RANDOM_NUMBER_PROVIDER,
  RECONNECT_CONFIG,
  WEBSOCKET_TEST_SCHEDULER,
} from './websocket.config';
import { ReconnectConfig } from './websocket.interface';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService implements OnDestroy {
  /**
   * Emits a signel (`undefined`) when the websocket connection status changes from 0 to 1 (i.e. disconnected to connected).
   * Ignores the first status change that happens when the websocket connection is established after the webapp shell loads.
   */
  reconnected$: Rx.Observable<void>;

  private sock: Socket;
  private connectionStatusSubject$ = new Rx.BehaviorSubject<number>(0);
  public connectionStatus$ = this.connectionStatusSubject$.asObservable();

  private fromServerStreamSubject$ = new Rx.Subject<
    WebsocketServerEvent<WebsocketServerEventTParam>
  >();
  fromServerStream$ = this.fromServerStreamSubject$.asObservable();

  private open = false;
  private channelSubscriptions = new Set<WebsocketChannel>();
  private resourcesSubscriptions = new MultiSet<WebsocketResource>();
  private onlineOfflineSubscriptions = new MultiSet<number>();
  private subscriptions = new Rx.Subscription();
  private unsubDebounceTime?: Timer; // TODO: T35544
  private connectRetryCount = 0;

  private TRACKING_RETRY_COUNT_THRESHOLD = 5;
  private TRACKING_RETRY_COUNT_DEBOUNCE = 10;

  constructor(
    @Inject(DATASTORE_CONFIG) private datastoreConfig: DatastoreConfig,
    @Inject(RECONNECT_CONFIG) private reconnectConfig: ReconnectConfig,
    private auth: Auth,
    private tracking: Tracking,
    private store$: Store<StoreState>,
    private timeUtils: TimeUtils,
    private ngZone: NgZone,
    private errorHandler: ErrorHandler,
    private pwa: Pwa,
    @Optional()
    @Inject(WEBSOCKET_TEST_SCHEDULER)
    private scheduler: Rx.SchedulerLike | null,
    @Optional()
    @Inject(RANDOM_NUMBER_PROVIDER)
    private randomNumberProvider: () => number,
  ) {
    this.create();

    if (this.randomNumberProvider === null) {
      this.randomNumberProvider = Math.random;
    }

    this.reconnected$ = this.hasReconnected(this.connectionStatus$);
    this.subscriptions.add(this.reconnected$.subscribe());
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  hasReconnected(
    connectionStatus$: Rx.Observable<number>,
  ): Rx.Observable<void> {
    return connectionStatus$.pipe(
      pairwise(),
      filter(
        ([prevOnlineState, currentOnlineState]) =>
          prevOnlineState === 0 && currentOnlineState === 1,
      ),
      // skip the initial change of status that happens when
      // the websocket connection is established for the first time
      skip(1),
      map(_ => undefined),
      delay(
        this.randomNumberProvider() *
          this.reconnectConfig.signalDelayMaxMilliseconds,
        this.scheduler ?? Rx.asapScheduler,
      ),
      publishReplay(1),
      refCount(),
    );
  }

  private create(isReconnect: boolean = false) {
    const authStateDistinct$ = this.auth.authState$.pipe(
      distinctUntilChanged(isEqual),
    );
    const createdWebsocketSubscription = authStateDistinct$.subscribe(auth => {
      this.open = false;

      // FIXME: handle logged-out / logging-out
      if (!auth) {
        return;
      }

      const closed = () => {
        if (!this.open) {
          return;
        }

        this.connectionStatusSubject$.next(
          this.connectionStatusSubject$.getValue() - 1,
        );
        this.open = false;
      };

      this.ngZone.runOutsideAngular(async () => {
        this.sock = new Socket(this.datastoreConfig.webSocketUrl);

        this.sock.onopen = () => {
          this.ngZone.run(() => {
            this.open = true;
            this.connectRetryCount = 0;
            this.connectionStatusSubject$.next(
              this.connectionStatusSubject$.getValue() + 1,
            );
            this.sendEvent(generateHeader(auth));
            this.subscribeToEvents();

            if (isReconnect) {
              this.tracking.trackCustomEvent(
                'WS.ON_OPEN_RECONNECT',
                'WS_CONN_EVENT',
              );
            } else {
              this.tracking.trackCustomEvent(
                'WS.ON_OPEN_INITIAL',
                'WS_CONN_EVENT',
              );
            }
          });
        };

        // Only send events with actual messages, not handshaking messages.
        this.sock.onmessage = event => {
          this.ngZone.run(() => {
            this.processEvent(JSON.parse(event.data), auth.userId);
          });
        };

        this.sock.onerror = event => {
          // log as a sentry breadcrumb
          console.error('Websocket connection error', event);
          this.errorHandler.handleError(
            new Error('Websocket connection error'),
          );
          if (
            this.shouldTrackWebsocketDisconnectionEvent(this.connectRetryCount)
          ) {
            this.tracking.trackCustomEvent('WS.ON_ERROR', 'WS_CONN_EVENT');
          }
        };

        this.sock.onclose = event => {
          this.ngZone.run(() => {
            closed();
            if (event.wasClean) {
              this.fromServerStreamSubject$.complete();
            } else {
              // retryDelay is capped to ~30 seconds
              const retryDelayFactor = Math.min(this.connectRetryCount, 5);
              // FIXME: use global error toast service to display something when
              // the websocket is reconnecting (and let the user force reconnect?)
              const retryDelay =
                2 ** retryDelayFactor * 1000 + Math.round(Math.random() * 1000);
              console.warn(
                `websocket disconnected, retrying in ${retryDelay}ms (retryCount: ${this.connectRetryCount})`,
              );

              if (
                this.shouldTrackWebsocketDisconnectionEvent(
                  this.connectRetryCount,
                )
              ) {
                this.tracking.trackCustomEvent('WS.ON_CLOSE', 'WS_CONN_EVENT', {
                  retryDelay,
                  retryCount: this.connectRetryCount,
                });
              }
              // race between the retry timeout & the online event..
              Rx.race(
                this.timeUtils.rxTimer(retryDelay).pipe(
                  tap(() => {
                    this.connectRetryCount++;
                    console.warn('reconnecting (timeout)...');
                    if (
                      this.shouldTrackWebsocketDisconnectionEvent(
                        this.connectRetryCount,
                      )
                    ) {
                      this.tracking.trackCustomEvent(
                        'WS.ON_CLOSE.RETRY_DELAY_ELAPSED',
                        'WS_CONN_EVENT',
                      );
                    }
                  }),
                  mapTo(true),
                ),
                Rx.fromEvent(window, 'online').pipe(
                  tap(() => {
                    this.connectRetryCount = 0;
                    console.warn('reconnecting (online event)...');
                    if (
                      this.shouldTrackWebsocketDisconnectionEvent(
                        this.connectRetryCount,
                      )
                    ) {
                      this.tracking.trackCustomEvent(
                        'WS.ON_CLOSE.WINDOW_ONLINE',
                        'WS_CONN_EVENT',
                      );
                    }
                  }),
                  mapTo(true),
                ),
              )
                // ..take the first one..
                .pipe(first())
                .toPromise()
                .then(() => {
                  this.ngZone.runOutsideAngular(() => {
                    if (
                      this.shouldTrackWebsocketDisconnectionEvent(
                        this.connectRetryCount,
                      )
                    ) {
                      this.tracking.trackCustomEvent(
                        'WS.ON_CLOSE.RECONNECTING',
                        'WS_CONN_EVENT',
                      );
                    }
                    // and re-create the websocket.
                    this.create(true);
                  });
                });
            }
          });
        };

        // If in native apps, also attempt to reconnect
        // when the app is brought to foreground.
        if (this.pwa.isNative()) {
          const { App } = await this.pwa.capacitorPlugins();
          App.addListener('appStateChange', (appState: AppState) => {
            if (
              // if app becomes active
              appState.isActive &&
              // and the websocket is alive or reconnecting
              this.sock.readyState() !== State.OPEN &&
              this.sock.readyState() !== State.CONNECTING
            ) {
              console.warn('Websocket disconnected due to app state...');
              if (
                this.shouldTrackWebsocketDisconnectionEvent(
                  this.connectRetryCount,
                )
              ) {
                this.tracking.trackCustomEvent(
                  'WS.ON_CLOSE.AFTER_APP_ACTIVE',
                  'WS_CONN_EVENT',
                );
              }
              this.ngZone.run(() => {
                // Mark as closed
                closed();
                this.ngZone.runOutsideAngular(() => {
                  // And try to reconnect
                  this.create(true);
                });
              });
            }
          });
        }
      });

      return () => {
        if (this.sock) {
          closed();
          this.ngZone.runOutsideAngular(() => {
            this.sock.close();
          });
        }
      };
    });
    this.subscriptions.add(createdWebsocketSubscription);
  }

  processEvent(
    event: WebsocketServerEvent<WebsocketServerEventTParam>,
    userId: string,
  ) {
    // Process websocket message
    if (isWebsocketMessage<BaseServerMessage>(event)) {
      this.processMessage(event, userId);
    }
    // Resubscribe to resource if receiving expiration notice
    if (isWebsocketSubscriptionExpiring(event)) {
      this.resourceResubscribe(event);
    }
  }

  sendEvent(message: SockMessageSend) {
    if (this.open) {
      this.ngZone.runOutsideAngular(() => {
        this.sock.send(message);
      });
    } else {
      console.error('Tried sending a message to a closed websocket.');
      this.tracking.trackCustomEvent(
        'WS.SEND_MSG_CLOSED_CONN',
        'WS_CONN_EVENT',
      );
    }
  }

  private resourceResubscribe(event: ExpiringSubscription) {
    this.sendEvent(generateResourcesSubMessage(event.body.resources));
  }

  private processMessage(
    event: WebsocketMessage<BaseServerMessage>,
    toUserId: string,
  ) {
    const { body } = event;

    const action: TypedAction = {
      type: 'WS_MESSAGE',
      no_persist: body.no_persist,
      payload: {
        ...body,
        toUserId, // all WebSocket messages are tied to the current user
      },
    };
    this.store$.dispatch(action);
    // Once the store is updated then we can let everyone know.
    this.fromServerStreamSubject$.next(event);
  }

  /* We buffer the user subscriptions and unsubscriptions by 1 second
   * to reduce backend load.
   * It's possible to have subscribed and unsubscriped within that buffer
   * so we remove such sub/unsub (and reverse) manually.
   */
  private subscribeToEvents() {
    // Subscribe to existing subscriptions
    const existingSubscriptions = this.onlineOfflineSubscriptions.values();
    if (existingSubscriptions.length) {
      this.sendEvent(generateOnlineOfflineSub(existingSubscriptions));
    }

    if (this.channelSubscriptions.size) {
      this.sendEvent(
        generateChannelSubMessage(Array.from(this.channelSubscriptions)),
      );
    }

    const existingResourcesSubscriptions = this.resourcesSubscriptions.values();
    if (existingResourcesSubscriptions.length) {
      this.sendEvent(
        generateResourcesSubMessage(existingResourcesSubscriptions),
      );
    }

    this.sendEvent(
      generateOnlineOfflineSub(this.onlineOfflineSubscriptions.values()),
    );
  }

  /**
   * Subscribes and unsubscribes to a list of resources by id.
   *
   * This is designed such that it is passed an observable of `ids$` and returns
   * a observable to be subscribed to such that:
   *  - when the return result is subscribed this will subscribe on the
   *    websocket to the resources in `ids$`
   *  - when the `ids$` change this will subscribe to new ids and unsubscribe
   *    from old ids as required
   *  - when the return result is unsubscribed it will unsubscribe on the
   *    websocket to all of `ids$`
   */
  subscribeToIds(
    collectionName: string,
    ids$: Rx.Observable<readonly string[]>,
  ): Rx.Observable<unknown> {
    switch (collectionName) {
      case 'onlineOffline':
        return this.subscribeOnlineOffline(ids$);

      case 'groups':
        return this.subscribeToResources(
          ids$.pipe(
            map(ids =>
              ids.map(id => ({
                id: toNumber(id),
                type: ResourceTypeApi.GROUP,
              })),
            ),
          ),
        );

      default:
        throw new Error(`Cannot subscribe to collection '${collectionName}'`);
    }
  }

  private subscribeOnlineOffline(userIds$: Rx.Observable<readonly string[]>) {
    // Watch the user id stream, calculate watch for changes,
    // subscribe/unsubscribe to any additions/removals respectively
    // and return the user status for the user who is subscribed to.
    // We add `undefined` to the end so we can get the last unsub.
    const incomingUserIds$ = Rx.concat(
      userIds$.pipe(
        distinctUntilChanged(),
        map(ids => ids.map(id => toNumber(id))),
        startWithEmptyList(),
      ),
      Rx.of([]),
    ).pipe(pairwise());

    // Work out which user ids are just added to this subscription.
    const addedUserIds$ = incomingUserIds$.pipe(
      map(([previous, current]) => setDiff(current, previous)),
    );

    // Work out which user ids are just removed to this subscription.
    const removedUserIds$ = incomingUserIds$.pipe(
      map(([previous, current]) => setDiff(previous, current)),
    );

    // Add new ids to subscription counts and return ones newly added to the global collection
    const newlyAddedUserIds$ = addedUserIds$.pipe(
      map(ids =>
        ids.filter(id => {
          this.onlineOfflineSubscriptions.add(id);
          return this.onlineOfflineSubscriptions.multiplicity(id) === 1;
        }),
      ),
    );

    // Add new ids to subscription counts and return ones newly added to the global collection
    const newlyRemovedUserIds$ = removedUserIds$.pipe(
      map(ids =>
        ids.filter(id => {
          if (this.onlineOfflineSubscriptions.multiplicity(id) === 0) {
            console.warn(`Tried removing user ${id} who is already removed.`);
          }
          this.onlineOfflineSubscriptions.remove(id);
          return this.onlineOfflineSubscriptions.multiplicity(id) === 0;
        }),
      ),
    );

    // Send websocket events and return an Rx.Observable.never()
    return Rx.NEVER.pipe(
      withLatestFrom(
        newlyAddedUserIds$.pipe(
          tap(ids => {
            if (this.open && ids.length) {
              this.sendEvent(generateOnlineOfflineSub(ids));
            }
          }),
        ),
        newlyRemovedUserIds$.pipe(
          tap(ids => {
            if (this.open && ids.length) {
              this.sendEvent(generateOnlineOfflineUnsub(ids));
            }
          }),
        ),
      ),
    );
  }

  /**
   * Subscribe to resources based on their ids.
   * To be used ONLY in this service as a automatic collection subscription mechanism.
   */
  private subscribeToResources(
    resources$: Rx.Observable<readonly WebsocketResource[]>,
  ) {
    const incomingResources$ = resources$.pipe(startWith([]), pairwise());

    // Work out which resources are just added to this subscription.
    const addedResources$ = incomingResources$.pipe(
      map(([previous, current]) => setDiff(current, previous)),
    );

    // Work out which resources are just removed to this subscription.
    const removedResources$ = incomingResources$.pipe(
      map(([previous, current]) => setDiff(previous, current)),
    );

    // Add new resources to subscriptions and return ones newly added
    const newlyAddedResources$ = addedResources$.pipe(
      map(resources =>
        resources.filter(resource => {
          this.resourcesSubscriptions.add(resource);
          return this.resourcesSubscriptions.multiplicity(resource) === 1;
        }),
      ),
    );

    // Remove resources from subscription and return ones newly removed
    const newlyRemovedResources$ = removedResources$.pipe(
      map(resources =>
        resources.filter(resource => {
          this.resourcesSubscriptions.remove(resource);
          return this.resourcesSubscriptions.multiplicity(resource) === 0;
        }),
      ),
    );

    // Send websocket events and return an Rx.Observable.never()
    return Rx.NEVER.pipe(
      withLatestFrom(
        newlyAddedResources$.pipe(
          tap(resources => {
            if (this.open && resources.length) {
              this.sendEvent(generateResourcesSubMessage(resources));
            }
          }),
        ),
        newlyRemovedResources$.pipe(
          tap(resources => {
            if (this.open && resources.length) {
              this.sendEvent(generateResourcesUnsubMessage(resources));
            }
          }),
        ),
      ),
    );
  }

  /**
   * This overrides the list of custom channels that the websocket will be listening to.
   */
  setChannels(channels: readonly WebsocketChannel[]) {
    const currentSubscriptions = Array.from(this.channelSubscriptions);
    const arrayDiff = (
      a: readonly WebsocketChannel[],
      b: readonly WebsocketChannel[],
    ) => a.filter(x => !b.includes(x));

    this.unsubscribeChannels(arrayDiff(currentSubscriptions, channels));
    this.subscribeChannels(arrayDiff(channels, currentSubscriptions));
  }

  private subscribeChannels(channels: readonly WebsocketChannel[]) {
    if (channels.length) {
      if (this.open) {
        this.sendEvent(generateChannelSubMessage(channels));
      }
      channels.forEach(channel => this.channelSubscriptions.add(channel));
    }
  }

  private unsubscribeChannels(channels: readonly WebsocketChannel[]) {
    if (channels.length) {
      channels.forEach(channel => this.channelSubscriptions.delete(channel));

      /* TODO: Do this properly.
       * Unfortunately the websocket backend doesn't support unsubscribing
       * and so we need to disconnect and reconnect the websocket to unsub :(
       *
       * Ref T35544
       */
      if (this.unsubDebounceTime) {
        clearTimeout(this.unsubDebounceTime);
      }
      this.unsubDebounceTime = this.timeUtils.setTimeout(() => {
        this.unsubDebounceTime = undefined;
        this.ngZone.runOutsideAngular(() => {
          this.sock.close();
        });
        this.timeUtils.setTimeout(() => this.create(), 1000);

        // We should also disconnect the other websocket that libnotify has.
        const w = window as any;
        if (w.libnotify && w.libnotify.close) {
          w.libnotify.close();
        }
      }, 5000);
    }
  }

  /**
   * When retry count reaches TRACKING_RETRY_COUNT_THRESHOLD, send tracking request only when retry count is a multiple of TRACKING_RETRY_COUNT_DEBOUNCE.
   *
   * @param retryCount
   */
  private shouldTrackWebsocketDisconnectionEvent(retryCount: number): boolean {
    return (
      retryCount < this.TRACKING_RETRY_COUNT_THRESHOLD ||
      retryCount % this.TRACKING_RETRY_COUNT_DEBOUNCE === 0
    );
  }
}
